import { useEffect, useRef, useState, type ReactNode } from "react";
import { useBackLayer } from "@/lib/back-stack";
import { ChevronLeft, ChevronRight, Clock, Copy, KeyRound, MessageSquare, Pause, Pencil, Play, Plus, Trash2, Webhook } from "lucide-react";
import { Markdown } from "@/components/markdown";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { useCan } from "@/lib/auth";
import { describeCron, formatInZone } from "@/lib/cron-text";
import { formatDateTime, relativeTime } from "@/lib/format";
import type { AgentTrigger, AgentWithContext } from "@/lib/types";
import { cn } from "@/lib/utils";

const PRESETS: Array<{ label: string; schedule: string }> = [
  { label: "Every hour", schedule: "0 * * * *" },
  { label: "Weekdays 9:00", schedule: "0 9 * * 1-5" },
  { label: "Daily 18:00", schedule: "0 18 * * *" },
  { label: "Mondays 8:00", schedule: "0 8 * * 1" },
];

const localZone = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
})();

const hookUrl = (token: string) => `${window.location.origin}/api/hooks/${token}`;

/**
 * An agent's triggers: webhooks and schedules that wake it without anyone asking.
 * Each firing is a turn in its chat like any other. A webhook's URL is shown once,
 * at creation or on rotation — it is the credential, and only its hash is kept.
 * Tapping a trigger opens its details inside the dialog (Back returns to the list):
 * a list card clamps the prompt to two lines, and the prompt is the whole point.
 */
export function AgentTriggersDialog({
  agent,
  open,
  onOpenChange,
  now,
  handles,
}: {
  agent: AgentWithContext;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  now: number;
  handles: Set<string>;
}) {
  const canManage = useCan("admin");
  const [triggers, setTriggers] = useState<AgentTrigger[]>([]);
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [revealed, setRevealed] = useState<{ triggerId: string; url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const selected = triggers.find((trigger) => trigger.id === selectedId) ?? null;
  // Each step in is a step back out: from the form to the details, from the details to the list.
  useBackLayer(open && selectedId !== null, () => openDetails(null));
  useBackLayer(open && editing, () => setEditing(false));
  useBackLayer(open && creating, () => setCreating(false));

  const load = async () => {
    try {
      setTriggers((await api.agentTriggers(agent.id)).triggers);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not load the triggers");
    }
  };
  useEffect(() => {
    if (!open) return;
    setRevealed(null);
    setError(null);
    setCreating(false);
    setSelectedId(null);
    setEditing(false);
    void load();
  }, [open, agent.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each view starts at its top, not at wherever the list was scrolled to.
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [selectedId, editing]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "That did not work");
    }
  };

  const openDetails = (triggerId: string | null) => {
    setSelectedId(triggerId);
    setEditing(false);
    setCreating(false);
    setError(null);
  };

  const toggle = (trigger: AgentTrigger) => act(() => api.updateAgentTrigger(trigger.id, { enabled: !trigger.enabled }));
  const rotate = (trigger: AgentTrigger) => {
    if (!window.confirm("Issue a new URL? The current one stops working at once.")) return;
    void act(async () => {
      const { token } = await api.updateAgentTrigger(trigger.id, { rotateToken: true });
      if (token) setRevealed({ triggerId: trigger.id, url: hookUrl(token) });
    });
  };
  const remove = (trigger: AgentTrigger) => {
    if (!window.confirm(`Delete the trigger "${trigger.name}"?`)) return;
    void act(async () => {
      await api.deleteAgentTrigger(trigger.id);
      if (selectedId === trigger.id) openDetails(null);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={contentRef} className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        {selected ? (
          <DialogHeader>
            <Button variant="ghost" size="xs" className="-ml-2 self-start" onClick={() => openDetails(null)}>
              <ChevronLeft /> All triggers
            </Button>
            <DialogTitle className="flex items-center gap-2 pr-8">
              <TriggerIcon trigger={selected} />
              <span className="min-w-0 break-words">{selected.name}</span>
            </DialogTitle>
            <DialogDescription className="sr-only">Details of the trigger {selected.name}</DialogDescription>
          </DialogHeader>
        ) : (
          <DialogHeader>
            <DialogTitle>Triggers for {agent.name}</DialogTitle>
            <DialogDescription>
              Wake {agent.name} to do something on its own: when a webhook is called (a GitHub push, a CI result) or on a
              schedule (a nightly crawl). Each run shows up in its chat. Tap one for its details.
            </DialogDescription>
          </DialogHeader>
        )}

        {revealed ? (
          <div className="space-y-1.5 rounded-lg border border-primary/40 bg-primary/8 p-3 text-[12px]">
            <p className="flex items-center gap-1.5 font-medium">
              <KeyRound className="size-3.5 text-primary" /> Webhook URL — copy it now, it is not shown again
            </p>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 break-all rounded bg-background px-2 py-1 font-mono text-[11px]">{revealed.url}</code>
              <Button size="icon-sm" variant="outline" aria-label="Copy URL" onClick={() => void navigator.clipboard?.writeText(revealed.url)}>
                <Copy />
              </Button>
            </div>
            <p className="text-muted-foreground">
              POST to it with any body (JSON is passed to the agent as data). For GitHub, use it as the payload URL with
              content type <span className="font-mono">application/json</span>.
            </p>
          </div>
        ) : null}

        {selected ? (
          editing ? (
            <TriggerForm
              initial={selected}
              onCancel={() => setEditing(false)}
              onSubmit={async (input) => {
                const patch: Parameters<typeof api.updateAgentTrigger>[1] = { name: input.name, instruction: input.instruction };
                // Only a changed schedule is sent: sending one re-arms the next run from now.
                if (input.schedule !== undefined && input.schedule !== selected.schedule) patch.schedule = input.schedule;
                if (input.timezone !== undefined && input.timezone !== selected.timezone) patch.timezone = input.timezone;
                await api.updateAgentTrigger(selected.id, patch);
                setEditing(false);
                await load();
              }}
            />
          ) : (
            <TriggerDetails
              trigger={selected}
              agentName={agent.name}
              now={now}
              handles={handles}
              canManage={canManage}
              onEdit={() => setEditing(true)}
              onToggle={() => void toggle(selected)}
              onRotate={() => rotate(selected)}
              onDelete={() => remove(selected)}
              onOpenChat={() => onOpenChange(false)}
            />
          )
        ) : (
          <>
            <div className="space-y-2">
              {triggers.length === 0 && !creating ? (
                <p className="rounded-lg border border-dashed border-border px-3 py-5 text-center text-[12.5px] text-muted-foreground">
                  No triggers yet.
                </p>
              ) : null}
              {triggers.map((trigger) => (
                <div
                  key={trigger.id}
                  data-trigger-id={trigger.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Details of ${trigger.name}`}
                  onClick={() => openDetails(trigger.id)}
                  onKeyDown={(event) => {
                    // Enter on a Disable button inside bubbles here too; only the card itself opens.
                    if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) return;
                    event.preventDefault();
                    openDetails(trigger.id);
                  }}
                  className={cn(
                    "cursor-pointer rounded-lg border border-border/80 p-3 transition-colors hover:border-ring/40 hover:bg-muted/40",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    !trigger.enabled && "opacity-70",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <TriggerIcon trigger={trigger} />
                    <p className="line-clamp-2 min-w-0 flex-1 break-words text-[13px] font-medium">{trigger.name}</p>
                    {canManage ? (
                      // The actions are inside the card; a tap on one must not also open the details.
                      <div className="flex items-center gap-0.5" onClick={(event) => event.stopPropagation()}>
                        <Button size="xs" variant="ghost" onClick={() => void toggle(trigger)}>
                          {trigger.enabled ? "Disable" : "Enable"}
                        </Button>
                        {trigger.kind === "webhook" ? (
                          <Button size="xs" variant="ghost" onClick={() => rotate(trigger)}>
                            New URL
                          </Button>
                        ) : null}
                        <Button size="icon-sm" variant="ghost" aria-label={`Delete ${trigger.name}`} onClick={() => remove(trigger)}>
                          <Trash2 />
                        </Button>
                      </div>
                    ) : null}
                    <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                  </div>
                  <p className="mt-1 line-clamp-2 text-[12px] text-muted-foreground">{trigger.instruction}</p>
                  <p className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                    {trigger.kind === "cron" ? (
                      <span className="font-mono">
                        {trigger.schedule} · {trigger.timezone}
                      </span>
                    ) : (
                      <span>webhook · URL shown once at creation</span>
                    )}
                    {trigger.kind === "cron" && trigger.enabled && trigger.nextRunAt ? (
                      <span>next {formatDateTime(trigger.nextRunAt)}</span>
                    ) : null}
                    <span>
                      fired {trigger.fireCount}×{trigger.lastFiredAt ? `, last ${relativeTime(trigger.lastFiredAt, now)}` : ""}
                    </span>
                    {trigger.lastResult ? <span className="min-w-0 truncate">· {trigger.lastResult}</span> : null}
                  </p>
                </div>
              ))}
            </div>

            {canManage ? (
              creating ? (
                <TriggerForm
                  onCancel={() => setCreating(false)}
                  onSubmit={async (input) => {
                    const { token } = await api.createAgentTrigger(agent.id, input);
                    setCreating(false);
                    if (token) setRevealed({ triggerId: "", url: hookUrl(token) });
                    await load();
                  }}
                />
              ) : (
                <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
                  <Plus /> Add a trigger
                </Button>
              )
            ) : (
              <p className="text-[11.5px] text-muted-foreground">Only an admin can add or change triggers.</p>
            )}
          </>
        )}
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </DialogContent>
    </Dialog>
  );
}

function TriggerIcon({ trigger }: { trigger: AgentTrigger }) {
  return trigger.kind === "cron" ? <Clock className="size-4 shrink-0 text-primary" /> : <Webhook className="size-4 shrink-0 text-primary" />;
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] gap-2 py-1.5 text-[12.5px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

/** One trigger in full: the whole prompt, the schedule in words and its next runs, and its history. */
function TriggerDetails({
  trigger,
  agentName,
  now,
  handles,
  canManage,
  onEdit,
  onToggle,
  onRotate,
  onDelete,
  onOpenChat,
}: {
  trigger: AgentTrigger;
  agentName: string;
  now: number;
  handles: Set<string>;
  canManage: boolean;
  onEdit: () => void;
  onToggle: () => void;
  onRotate: () => void;
  onDelete: () => void;
  onOpenChat: () => void;
}) {
  const zone = trigger.timezone ?? "UTC";
  const [runs, setRuns] = useState<string[]>([]);
  useEffect(() => {
    if (trigger.kind !== "cron" || !trigger.schedule || !trigger.enabled) return setRuns([]);
    let live = true;
    api
      .previewSchedule(trigger.schedule, zone, 5)
      .then(({ runs }) => live && setRuns(runs))
      .catch(() => live && setRuns([]));
    return () => {
      live = false;
    };
  }, [trigger.kind, trigger.schedule, zone, trigger.enabled]);
  const words = trigger.kind === "cron" ? describeCron(trigger.schedule) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="outline">{trigger.kind === "cron" ? "Schedule" : "Webhook"}</Badge>
        {trigger.enabled ? <Badge tint="var(--kind-done)">Enabled</Badge> : <Badge variant="secondary">Disabled</Badge>}
        {canManage ? (
          <div className="ml-auto flex flex-wrap items-center gap-1">
            <Button size="xs" variant="outline" onClick={onEdit}>
              <Pencil /> Edit
            </Button>
            <Button size="xs" variant="outline" onClick={onToggle}>
              {trigger.enabled ? <Pause /> : <Play />} {trigger.enabled ? "Disable" : "Enable"}
            </Button>
            {trigger.kind === "webhook" ? (
              <Button size="xs" variant="outline" onClick={onRotate}>
                <KeyRound /> New URL
              </Button>
            ) : null}
            <Button size="icon-sm" variant="ghost" aria-label={`Delete ${trigger.name}`} onClick={onDelete}>
              <Trash2 />
            </Button>
          </div>
        ) : null}
      </div>

      <section className="space-y-1.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">What it does each time</h3>
        <div className="rounded-lg border border-border/80 bg-muted/30 px-3 py-2.5 text-[13px]">
          <Markdown text={trigger.instruction} handles={handles} />
        </div>
      </section>

      <section>
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {trigger.kind === "cron" ? "Schedule" : "Webhook"}
        </h3>
        <dl className="divide-y divide-border/60">
          {trigger.kind === "cron" ? (
            <>
              <DetailRow label="Runs">
                {words ?? "Custom schedule"}
                <span className="text-muted-foreground"> · {zone}</span>
              </DetailRow>
              <DetailRow label="Cron">
                <span className="font-mono">{trigger.schedule}</span>
              </DetailRow>
              <DetailRow label="Jitter">
                <span className="text-muted-foreground">None: fires within about 20 seconds of the minute</span>
              </DetailRow>
              <DetailRow label="Next runs">
                {!trigger.enabled ? (
                  <span className="text-muted-foreground">None while disabled</span>
                ) : runs.length > 0 ? (
                  <ul className="space-y-0.5">
                    {runs.map((run) => {
                      // Compared as text, not by zone name: Chromium calls Kolkata "Asia/Calcutta".
                      const there = formatInZone(run, zone);
                      const here = formatDateTime(run);
                      return (
                        <li key={run}>
                          {there}
                          {there !== here ? <span className="text-muted-foreground"> · {here} your time</span> : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <span className="text-muted-foreground">…</span>
                )}
              </DetailRow>
            </>
          ) : (
            <>
              <DetailRow label="URL">
                <span className="text-muted-foreground">
                  Hidden. It was shown once, at creation, and only its hash is kept.{" "}
                  {canManage ? "New URL issues another; the current one stops working at once." : null}
                </span>
              </DetailRow>
              <DetailRow label="Calling it">
                <span className="text-muted-foreground">POST with any body; JSON is passed to {agentName} as data.</span>
              </DetailRow>
            </>
          )}
        </dl>
      </section>

      <section>
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">History</h3>
        <dl className="divide-y divide-border/60">
          <DetailRow label="Fired">{trigger.fireCount}×</DetailRow>
          <DetailRow label="Last fired">
            {trigger.lastFiredAt ? (
              <>
                {formatDateTime(trigger.lastFiredAt)} <span className="text-muted-foreground">· {relativeTime(trigger.lastFiredAt, now)}</span>
              </>
            ) : (
              <span className="text-muted-foreground">Never</span>
            )}
          </DetailRow>
          {trigger.lastResult ? <DetailRow label="Last result">{trigger.lastResult}</DetailRow> : null}
          <DetailRow label="Created">{formatDateTime(trigger.createdAt)}</DetailRow>
          <DetailRow label="Updated">{formatDateTime(trigger.updatedAt)}</DetailRow>
        </dl>
        {trigger.fireCount > 0 ? (
          <Button variant="link" size="xs" className="mt-1 px-0" onClick={onOpenChat}>
            <MessageSquare /> Each run is in {agentName}'s chat
          </Button>
        ) : null}
      </section>
    </div>
  );
}

/** Creates a trigger, or edits one when given `initial` (its kind is then fixed). */
function TriggerForm({
  initial,
  onCancel,
  onSubmit,
}: {
  initial?: AgentTrigger;
  onCancel: () => void;
  onSubmit: (input: { name: string; kind: "webhook" | "cron"; instruction: string; schedule?: string; timezone?: string }) => Promise<void>;
}) {
  const [kind, setKind] = useState<"webhook" | "cron">(initial?.kind ?? "cron");
  const [name, setName] = useState(initial?.name ?? "");
  const [instruction, setInstruction] = useState(initial?.instruction ?? "");
  const [schedule, setSchedule] = useState(initial?.schedule ?? "0 9 * * 1-5");
  const [timezone, setTimezone] = useState(initial?.timezone ?? localZone);
  const [preview, setPreview] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Say when it would run before it is saved: a cron expression is easy to get subtly wrong.
  useEffect(() => {
    if (kind !== "cron") return;
    const handle = setTimeout(() => {
      api
        .previewSchedule(schedule, timezone)
        .then(({ runs }) => {
          setPreview(runs);
          setProblem(null);
        })
        .catch((cause) => {
          setPreview([]);
          setProblem(cause instanceof ApiError ? cause.message : "Not a valid schedule");
        });
    }, 300);
    return () => clearTimeout(handle);
  }, [kind, schedule, timezone]);

  const submit = async () => {
    setSaving(true);
    setProblem(null);
    try {
      await onSubmit({ name, kind, instruction, ...(kind === "cron" ? { schedule, timezone } : {}) });
    } catch (cause) {
      setProblem(cause instanceof ApiError ? cause.message : initial ? "Could not save the trigger" : "Could not create the trigger");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      {initial ? null : (
        <div className="flex gap-1.5">
          {(["cron", "webhook"] as const).map((option) => (
            <Button key={option} size="xs" variant={kind === option ? "secondary" : "ghost"} onClick={() => setKind(option)}>
              {option === "cron" ? <Clock /> : <Webhook />} {option === "cron" ? "Schedule" : "Webhook"}
            </Button>
          ))}
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="trigger-name">Name</Label>
        <Input
          id="trigger-name"
          value={name}
          placeholder={kind === "cron" ? "Morning crawl" : "GitHub push"}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="trigger-instruction">What to do each time</Label>
        <Textarea
          id="trigger-instruction"
          rows={initial ? 10 : 3}
          value={instruction}
          placeholder={
            kind === "cron"
              ? "Crawl the changelog pages listed in docs/sources.md and write what changed into docs/changes.md."
              : "Pull the latest main, run the tests, and put a card on the board for anything that fails."
          }
          onChange={(event) => setInstruction(event.target.value)}
        />
      </div>
      {kind === "cron" ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="trigger-schedule">Schedule (cron)</Label>
            <Input id="trigger-schedule" className="font-mono" value={schedule} onChange={(event) => setSchedule(event.target.value)} />
            <div className="flex flex-wrap gap-1">
              {PRESETS.map((preset) => (
                <button
                  key={preset.schedule}
                  onClick={() => setSchedule(preset.schedule)}
                  className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] text-muted-foreground hover:text-foreground"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="trigger-zone">Time zone</Label>
            <Input id="trigger-zone" value={timezone} onChange={(event) => setTimezone(event.target.value)} />
            {preview.length > 0 ? (
              <p className="text-[11px] leading-snug text-muted-foreground">Next: {preview.map((run) => formatDateTime(run)).join(" · ")}</p>
            ) : null}
          </div>
        </div>
      ) : initial ? null : (
        <p className="text-[11.5px] text-muted-foreground">
          You get a secret URL once it is created. Anything POSTed to it wakes the agent, with the body passed along as data.
        </p>
      )}
      {problem ? <p className="text-xs text-destructive">{problem}</p> : null}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" loading={saving} disabled={!name.trim() || !instruction.trim() || (kind === "cron" && problem !== null)} onClick={() => void submit()}>
          {initial ? "Save" : "Create"}
        </Button>
      </div>
    </div>
  );
}
